import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslatedContent } from '../../../../hooks/useTranslatedContent';
import { useArtistModalStore } from '../../../../stores/artistModalStore';
import { useTranslation } from 'react-i18next';
import { useMobileDetect } from '../../../../hooks/useMobileDetect';
import { CategoriesData } from '../../../../types/categories';

import { useCategories } from '../../../../hooks/useCategories';
import { useFilters } from '../../../../hooks/useFilters';
import { setupLinkHandlers } from '../../../../helpers/linkHandler';
import { useThemeStore } from '../../../../stores/themeStore';
import { useFormModalStore } from '../../../../stores/formStore';

import styles from './CategoryDetails.module.less';

export const CategoryDetails = () => {
  const { getTranslation } = useTranslatedContent();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useArtistModalStore();
  const { isMobile } = useMobileDetect();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [activeSection, setActiveSection] = useState<'info' | 'links'>('info');

  const contentRef = useRef<HTMLDivElement>(null);
  const { theme } = useThemeStore();
  const { data: categoriesData } = useCategories();
  const openArtistModal = useArtistModalStore((state) => state.openModal);
  const openFormModal = useFormModalStore((state) => state.openModal);

  const { selectedCategory } = useFilters();

  const typedCategoriesData = categoriesData as CategoriesData | undefined;

  const category = useMemo(() => {
    if (!selectedCategory || !typedCategoriesData) return null;
    return typedCategoriesData.categories.find(
      (category) => category.id === String(selectedCategory)
    );
  }, [selectedCategory, typedCategoriesData]);


  const handleProgramClick = useCallback((slug: string) => {
    navigate(`/about_project/${slug}/${category?.details?.[0]?.categories_details_id?.category_page[0].about_project_page_id.slug}`);
  }, [navigate, category]);


  useEffect(() => {
    if (!contentRef.current) return;
    const cleanup = setupLinkHandlers(contentRef.current, {
      openArtistModal,
      openFormModal,
      theme,
    });

    return () => {
      cleanup();

      if (contentRef.current) {
        const links = contentRef.current.querySelectorAll('a');
        links.forEach((link) => {
          link.classList.remove(
            styles.processed,
            styles.darkTheme,
            styles.artistLink,
            styles.formLink,
            styles.externalLink
          );

          const iconWrappers = link.querySelectorAll(`.${styles.test}`);
          iconWrappers.forEach((wrapper) => wrapper.remove());
        });
      }
    };
  }, [openArtistModal, openFormModal, theme]);


  if (!category || !category.details?.[0]?.categories_details_id) {
    return null;
  }

  const categoryDetails = category.details[0].categories_details_id;

  const description = getTranslation(categoryDetails, 'descriptions');
  const truncatedDescription = isMobile
    ? description.slice(0, 100) + (description.length > 100 ? '...' : '')
    : description;

  return (
    <div className={styles.categoryDetails}>
      {isMobile && (
        <div className={styles.toggleButtons}>
          <button
            onClick={() => setActiveSection('info')}
            className={`${styles.toggleButton} ${activeSection === 'info' ? styles.active : ''}`}
          >
            {t('Info')}
          </button>
          <button
            onClick={() => setActiveSection('links')}
            className={`${styles.toggleButton} ${activeSection === 'links' ? styles.active : ''}`}
          >
            {t('Links')}
          </button>
        </div>
      )}

      {(!isMobile || activeSection === 'info') && (
        <div className={styles.categoryDetails__block}>
          <div
            ref={contentRef}
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html:
                showFullDescription || !isMobile
                  ? description
                  : truncatedDescription,
            }}
          />
          {isMobile && description.length > 150 && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className={styles.loadMoreButton}
            >
              {showFullDescription ? t('Less') : t('More')}
            </button>
          )}
        </div>
      )}

      {(!isMobile || activeSection === 'links') && (
        <div>
          {category.details?.[0]?.categories_details_id?.category_page.length > 0 && (
            <button className={styles.projectLink} onClick={() => handleProgramClick(category.slug)}>
              {t('About the direction')}
            </button>
          )}

          {categoryDetails.artists && categoryDetails.artists.length > 0 && (
            <div className={styles.artistsList}>
              {categoryDetails.artists.map((artist) => (
                <button
                  key={artist.artist_info_id.slug}
                  onClick={() => openModal(artist.artist_info_id.slug)}
                  className={styles.categoryDetails__artistLink}
                >
                  {getTranslation(artist.artist_info_id, 'artist_name')}
                </button>
              ))}
            </div>
          )}

          {categoryDetails.link && categoryDetails.link.length > 0 && (
            <ul>
              {categoryDetails.link.map(({ title, link }, index) => (
                <li key={index}>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.categoryDetails__artistLink}
                  >
                    {title}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
